/**
 * Module dependencies.
 */

import { Loading } from 'src/components/core/loading';
import { NavbarTheme } from 'src/types/app';
import { ReactNode, useEffect, useState } from 'react';
import { ifProp } from 'styled-tools';
import { useBodyScroll } from '@untile/react-components';
import { useNavbarTheme } from 'src/providers/navbar-theme';
import { useRouter } from 'next/router';
import styled from 'styled-components';

/**
 * `Animation` type.
 */

type Animation = 'start' | 'end';

/**
 * `Props` type.
 */

type Props = {
  children: ReactNode;
  navbarTheme: NavbarTheme;
};

/**
 * `Wrapper` styled component.
 */

const Wrapper = styled.div<{ invisible: boolean }>`
  opacity: ${ifProp('invisible', 0, 1)};
  transition: opacity var(--transition-default);
`;

/**
 * `LoadingWrapper` styled component.
 */

const LoadingWrapper = styled.div`
  background-color: var(--color-gray200);
  height: 100vh;
  left: 0;
  position: fixed;
  top: 0;
  width: 100vw;
  z-index: var(--z-index-loading);
`;

/**
 * `StyledLoading` styled component.
 */

const StyledLoading = styled(Loading).attrs({
  size: 50,
  stroke: 3
})`
  color: var(--color-primary);
`;

/**
 * `PageTransition` component.
 */

export function PageTransition({ children, navbarTheme }: Props) {
  const [animation, setAnimation] = useState<Animation>();
  const { setTheme } = useNavbarTheme();
  const router = useRouter();

  useEffect(() => {
    setAnimation('end');
    setTheme(navbarTheme);
  }, [navbarTheme, setTheme]);

  useEffect(() => {
    const handleRouteChangeStart = ({ shallow }: any) => {
      if (shallow) {
        return;
      }

      setAnimation('start');
    };

    router.events.on('routeChangeStart', handleRouteChangeStart);

    return () => {
      router.events.off('routeChangeStart', handleRouteChangeStart);
    };
  }, [router.events, router.pathname]);

  useEffect(() => {
    function handleRouteChangeComplete() {
      setAnimation('end');
      setTheme(navbarTheme);
    }

    router.events.on('routeChangeComplete', handleRouteChangeComplete);
    router.events.on('routeChangeError', handleRouteChangeComplete);

    return () => {
      router.events.off('routeChangeComplete', handleRouteChangeComplete);
      router.events.on('routeChangeError', handleRouteChangeComplete);
    };
  }, [navbarTheme, router.events, setTheme]);

  useBodyScroll({ off: animation === 'start' });

  return (
    <>
      <Wrapper invisible={animation === 'start'}>{children}</Wrapper>

      {animation === 'start' && (
        <LoadingWrapper>
          <StyledLoading />
        </LoadingWrapper>
      )}
    </>
  );
}
